import { useEffect, useMemo, useRef, useState } from 'react';
import { Puzzle, ReplayEvent } from '../../shared/types';
import { CellState, validate, computeAutoXs } from '../game';
import { api } from '../api';
import { formatTime } from '../utils';

const COLOR_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'];

interface ReplayData {
  events: ReplayEvent[];
  playerName: string;
  timeMs: number;
  usedAutoXs: boolean;
}

interface QueenMarker {
  pct: number;
  colorIdx: number;
}

function computeQueenMarkers(events: ReplayEvent[], puzzle: Puzzle, totalMs: number): QueenMarker[] {
  if (totalMs === 0) return [];
  const markers: QueenMarker[] = [];
  let board: CellState[][] = Array.from({ length: puzzle.size }, () => Array(puzzle.size).fill(null));
  const stack: CellState[][][] = [];

  for (const ev of events) {
    if (ev.type === 'click') {
      const next = board.map(r => [...r]) as CellState[][];
      const prev = next[ev.row][ev.col];
      next[ev.row][ev.col] = prev === null ? 'X' : prev === 'X' ? 'queen' : null;
      if (next[ev.row][ev.col] === 'queen') {
        const colorIdx = COLOR_LABELS.indexOf(puzzle.colorRegions[ev.row][ev.col]);
        markers.push({ pct: Math.min(ev.elapsedMs / totalMs * 100, 100), colorIdx });
      }
      stack.push(board);
      board = next;
    } else if (ev.type === 'drag') {
      const next = board.map(r => [...r]) as CellState[][];
      for (const { row, col } of ev.cells) {
        if (next[row][col] === null) next[row][col] = 'X';
      }
      stack.push(board);
      board = next;
    } else if (ev.type === 'undo') {
      if (stack.length > 0) board = stack.pop()!;
    } else if (ev.type === 'clear') {
      stack.push(board);
      board = Array.from({ length: puzzle.size }, () => Array(puzzle.size).fill(null)) as CellState[][];
    }
  }

  return markers;
}

/**
 * Board state after the first `upTo` events. Exported for the test that pins its handling of
 * event types it does not know about (`setting`), which must be a no-op rather than a desync.
 */
export function computeBoardAt(events: ReplayEvent[], size: number, upTo: number): CellState[][] {
  let board: CellState[][] = Array.from({ length: size }, () => Array(size).fill(null));
  const stack: CellState[][][] = [];

  for (let i = 0; i < upTo; i++) {
    const ev = events[i];
    if (ev.type === 'click') {
      const next = board.map(r => [...r]) as CellState[][];
      const prev = next[ev.row][ev.col];
      next[ev.row][ev.col] = prev === null ? 'X' : prev === 'X' ? 'queen' : null;
      stack.push(board);
      board = next;
    } else if (ev.type === 'drag') {
      const next = board.map(r => [...r]) as CellState[][];
      for (const { row, col } of ev.cells) {
        if (next[row][col] === null) next[row][col] = 'X';
      }
      stack.push(board);
      board = next;
    } else if (ev.type === 'undo') {
      if (stack.length > 0) board = stack.pop()!;
    } else if (ev.type === 'clear') {
      stack.push(board);
      board = Array.from({ length: size }, () => Array(size).fill(null)) as CellState[][];
    }
  }

  return board;
}

interface Props {
  entryId: number;
  puzzleId: number;
  isCompleted: boolean;
  isSkipped: boolean;
  completionsLoaded: boolean;
  onSkip: () => void;
  onBack: () => void;
}

export function ReplayView({ entryId, puzzleId, isCompleted, isSkipped, completionsLoaded, onSkip, onBack }: Props) {
  const [spoilerDismissed, setSpoilerDismissed] = useState(false);
  // Gate status is only known once completions have loaded (or user has dismissed)
  const gateKnown = completionsLoaded || spoilerDismissed;
  const spoilerGate = gateKnown && !isCompleted && !isSkipped && !spoilerDismissed;
  const [replay, setReplay] = useState<ReplayData | null>(null);
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [eventIndex, setEventIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(2);

  // Always fetch immediately — parallel with completions loading
  useEffect(() => {
    let canceled = false;
    Promise.all([api.getReplay(entryId), api.getPuzzle(puzzleId)])
      .then(([r, p]) => { if (!canceled) { setReplay(r); setPuzzle(p); } })
      .catch(() => { if (!canceled) setLoadError(true); });
    return () => { canceled = true; };
  }, [entryId, puzzleId]);

  // Autoplay once data is ready and gate is resolved (not blocking)
  useEffect(() => {
    if (replay && puzzle && gateKnown && !spoilerGate) setPlaying(true);
  }, [replay, puzzle, gateKnown, spoilerGate]);

  const finished = replay !== null && eventIndex >= replay.events.length;

  useEffect(() => {
    if (finished) setPlaying(false);
  }, [finished]);

  useEffect(() => {
    if (!playing || !replay || finished) return;
    // Use current animation position so seek works: delay = time until next event from *now*
    const delay = (replay.events[eventIndex].elapsedMs - displayTimeMsRef.current) / speed;
    const id = setTimeout(() => setEventIndex(i => i + 1), Math.max(delay, 0));
    return () => clearTimeout(id);
  }, [playing, eventIndex, replay, speed, finished]);

  // Continuously interpolate replay time between events for a smooth progress bar.
  // origin tracks where in replay-time we were when the loop last (re)started.
  // speed is read via a ref so changing it doesn't restart the loop (which would jump).
  const [displayTimeMs, setDisplayTimeMs] = useState(0);
  const displayTimeMsRef = useRef(0);
  const originRef = useRef<{ startWall: number; startReplay: number } | null>(null);
  const speedRef = useRef(speed);

  useEffect(() => { speedRef.current = speed; }, [speed]);

  // When speed changes, re-anchor the origin to the current display position
  // so the bar doesn't jump to where it would have been at the new speed.
  useEffect(() => {
    if (originRef.current !== null) {
      originRef.current = { startWall: Date.now(), startReplay: displayTimeMsRef.current };
    }
  }, [speed]);

  // Animation loop — eventIndex is intentionally NOT a dependency. Each time an event
  // fires, eventIndex increments and React would tear down and restart the loop, snapping
  // startReplay back to the committed event time (behind where displayTimeMs already is).
  // Instead the loop runs uninterrupted; it always resumes from displayTimeMsRef.current.
  useEffect(() => {
    if (!playing || !replay) {
      originRef.current = null;
      return;
    }
    originRef.current = { startWall: Date.now(), startReplay: displayTimeMsRef.current };
    const totalMs = replay.timeMs;
    let frameId: number;
    function tick() {
      if (!originRef.current) return; // preempted by seek
      const o = originRef.current;
      const t = Math.min(o.startReplay + (Date.now() - o.startWall) * speedRef.current, totalMs);
      displayTimeMsRef.current = t;
      setDisplayTimeMs(t);
      frameId = requestAnimationFrame(tick);
    }
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, replay]);

  const board = useMemo(() => {
    if (!puzzle || !replay) return null;
    return computeBoardAt(replay.events, puzzle.size, eventIndex);
  }, [puzzle, replay, eventIndex]);

  const queenMarkers = useMemo(() => {
    if (!puzzle || !replay) return [];
    return computeQueenMarkers(replay.events, puzzle, replay.timeMs);
  }, [puzzle, replay]);

  function togglePlay() {
    if (finished) {
      displayTimeMsRef.current = 0;
      setDisplayTimeMs(0);
      setEventIndex(0);
      setPlaying(true);
    } else {
      setPlaying(p => !p);
    }
  }

  const progressRef = useRef<HTMLDivElement>(null);
  const wasPlayingRef = useRef(false);

  function seekTo(timeMs: number) {
    let idx = 0;
    while (idx < replay!.events.length && replay!.events[idx].elapsedMs <= timeMs) idx++;
    setEventIndex(idx);
    displayTimeMsRef.current = timeMs;
    setDisplayTimeMs(timeMs);
  }

  function handleProgressMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (!progressRef.current || !replay) return;
    e.preventDefault();
    wasPlayingRef.current = playing;
    originRef.current = null; // stop rAF from overwriting displayTimeMsRef before next render
    setPlaying(false);
    const bar = progressRef.current;
    const getSeekMs = (x: number) => {
      const rect = bar.getBoundingClientRect();
      return Math.max(0, Math.min(1, (x - rect.left) / rect.width)) * replay.timeMs;
    };
    seekTo(getSeekMs(e.clientX));
    const onMove = (ev: MouseEvent) => seekTo(getSeekMs(ev.clientX));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (wasPlayingRef.current) setPlaying(true);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  function handleProgressTouchStart(e: React.TouchEvent<HTMLDivElement>) {
    if (!progressRef.current || !replay) return;
    wasPlayingRef.current = playing;
    originRef.current = null;
    setPlaying(false);
    const bar = progressRef.current;
    const getSeekMs = (x: number) => {
      const rect = bar.getBoundingClientRect();
      return Math.max(0, Math.min(1, (x - rect.left) / rect.width)) * replay.timeMs;
    };
    seekTo(getSeekMs(e.touches[0].clientX));
    const onMove = (ev: TouchEvent) => { ev.preventDefault(); seekTo(getSeekMs(ev.touches[0].clientX)); };
    const onEnd = () => {
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      if (wasPlayingRef.current) setPlaying(true);
    };
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
  }

  if (!gateKnown) return <p className="loading">Loading…</p>;

  if (spoilerGate) {
    return (
      <div className="spoiler-gate">
        <div className="spoiler-gate-box">
          <p>You haven't yet completed this puzzle on GibyQueens. To avoid spoilers, would you like to complete it first?</p>
          <div className="spoiler-gate-buttons">
            <button className="btn-primary" onClick={onBack}>Go To Puzzle</button>
            <button className="btn-secondary" onClick={() => { onSkip(); setSpoilerDismissed(true); }}>View Replay</button>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) return <p className="error">Failed to load replay.</p>;
  if (!replay || !puzzle || !board) return <p className="loading">Loading replay…</p>;

  const { size, colorRegions } = puzzle;
  const result = validate(board, colorRegions);
  const autoXCells = replay.usedAutoXs ? computeAutoXs(board, colorRegions) : null;
  const progress = replay.timeMs === 0 ? 0 : displayTimeMs / replay.timeMs * 100;

  const cells = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      const colorIdx = COLOR_LABELS.indexOf(colorRegions[r][c]);
      const cellState = board[r][c];
      const invalid = cellState === 'queen' && result.invalidCells.has(`${r},${c}`);
      const isAutoX = cellState === null && (autoXCells?.has(`${r},${c}`) ?? false);
      const isCrosshatch = result.crosshatchCells.has(`${r},${c}`);

      const cls = [
        'cell',
        `color-${colorIdx}`,
        r === 0 || colorRegions[r - 1][c] !== colorRegions[r][c] ? 'border-top' : '',
        c === 0 || colorRegions[r][c - 1] !== colorRegions[r][c] ? 'border-left' : '',
        r === size - 1 || colorRegions[r + 1]?.[c] !== colorRegions[r][c] ? 'border-bottom' : '',
        c === size - 1 || colorRegions[r][c + 1] !== colorRegions[r][c] ? 'border-right' : '',
        cellState === 'X' ? 'state-x' : '',
        cellState === 'queen' ? 'state-queen' : '',
        invalid ? 'state-invalid' : '',
        isAutoX ? 'state-auto-x' : '',
        isCrosshatch ? 'crosshatch' : '',
      ].filter(Boolean).join(' ');

      cells.push(
        <div key={`${r},${c}`} className={cls}>
          {cellState === 'X' ? '✕' : cellState === 'queen' ? '♛' : isAutoX ? '✕' : ''}
        </div>
      );
    }
  }

  return (
    <div className="replay-viewer">
      <button className="btn-back" onClick={onBack}>← Leaderboard</button>
      <div className="replay-header">
        <span className="replay-title">
          Replay: <strong>{replay.playerName}</strong> — {formatTime(replay.timeMs)}
        </span>
      </div>
      <div
        className="board replay-board"
        style={{ '--board-size': String(size) } as React.CSSProperties}
      >
        {cells}
      </div>
      <div className="replay-controls">
        <button className="btn-primary" onClick={togglePlay}>
          {finished ? '↺ Restart' : playing ? '⏸ Pause' : '▶ Play'}
        </button>
        <label>
          Speed:
          <select value={speed} onChange={e => setSpeed(Number(e.target.value))}>
            <option value={1}>1×</option>
            <option value={2}>2×</option>
            <option value={4}>4×</option>
          </select>
        </label>
        <span className="replay-current-time">{formatTime(displayTimeMs)}</span>
      </div>
      <div
        className="replay-progress-wrap"
        ref={progressRef}
        onMouseDown={handleProgressMouseDown}
        onTouchStart={handleProgressTouchStart}
      >
        <div className="replay-progress">
          <div className="replay-progress-fill" style={{ width: `${progress}%` }} />
          {queenMarkers.map((m, i) => (
            <div
              key={i}
              className="replay-progress-marker"
              style={{ left: `${m.pct}%`, background: `var(--c${m.colorIdx})`, color: `var(--c${m.colorIdx})` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
